import type { Cluster } from "~/types";
import { formatGitSha } from "~/utils";

// Clusters carry a `tags` map of dimension -> value from the control plane; a
// new backend tag automatically becomes a new group-by dimension.

// Well-known dims sort first; custom keys follow alphabetically.
const DIMENSION_ORDER = ["kind", "cloud", "region", "customer"];
const byDimension = (a: string, b: string) => {
  const rank = (k: string) => {
    const i = DIMENSION_ORDER.indexOf(k);
    return i === -1 ? DIMENSION_ORDER.length : i;
  };
  return rank(a) - rank(b) || a.localeCompare(b);
};

/** Group-by key for the upgrade operation's approval method. */
export const UPGRADE_APPROVAL_GROUP = "upgrade_approval";

// Groupings that stand on their own rather than on a backend tag, so they stay
// selectable no matter which tags the fleet currently carries.
export const FIXED_GROUPINGS = ["none", "version", UPGRADE_APPROVAL_GROUP];

export function valueFor(c: Cluster, dim: string): string | null {
  return c.tags?.[dim] ?? null;
}

export function dimensionsFor(clusters: Cluster[]): string[] {
  const keys = clusters.flatMap((c) => Object.keys(c.tags ?? {}));
  return [...new Set(keys)].sort(byDimension);
}

function sortValueFor(c: Cluster, sortBy: string): string | null {
  if (sortBy === "id") return c.id;
  return valueFor(c, sortBy);
}

// The deployed commit's timestamp, or null when the commit hasn't resolved.
function commitTime(c: Cluster): number | null {
  const date = c.git_details?.current_commit?.date;
  return date ? new Date(date).getTime() : null;
}

// The deployed commit's human-readable name.
function humanVersion(c: Cluster): string | null {
  const sha = c.git_details?.current_commit?.sha;
  return sha ? formatGitSha(sha) : null;
}

// Sort by cluster field (`id`, `updated_at`, `commit_time`) or a tag key
// (untagged last).
// Returns a new array.
export function sortClusters(clusters: Cluster[], sortBy: string): Cluster[] {
  const list = [...clusters];
  if (sortBy === "updated_at")
    return list.sort(
      (a, b) =>
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
    );
  if (sortBy === "commit_time")
    return list.sort((a, b) => {
      const ta = commitTime(a) ?? Number.MIN_SAFE_INTEGER;
      const tb = commitTime(b) ?? Number.MIN_SAFE_INTEGER;
      return tb - ta;
    });
  return list.sort((a, b) =>
    (sortValueFor(a, sortBy) ?? "~").localeCompare(
      sortValueFor(b, sortBy) ?? "~",
    ),
  );
}

// Grouping strategy for "group by version":
//  * one group per deployed version
//  * newest version first to match "sort by version"
export function versionGrouping(clusters: Cluster[]): {
  valueOf: (c: Cluster) => string | null;
  compare: (a: string, b: string) => number;
} {
  const timeByVersion = new Map<string, number>();
  for (const c of clusters) {
    const version = humanVersion(c);
    const time = commitTime(c);
    if (version !== null && time !== null) {
      timeByVersion.set(version, time);
    }
  }
  return {
    valueOf: humanVersion,
    compare: (a, b) => {
      const ta = timeByVersion.get(a) ?? Number.MIN_SAFE_INTEGER;
      const tb = timeByVersion.get(b) ?? Number.MIN_SAFE_INTEGER;
      return tb - ta;
    },
  };
}

// Grouping by kind runs SaaS first and BYOC last, the order operators think
// in, rather than alphabetically.
const KIND_ORDER = ["mt", "st", "byoc"];

export function kindGrouping(): {
  valueOf: (c: Cluster) => string | null;
  compare: (a: string, b: string) => number;
} {
  const rank = (kind: string) => {
    const i = KIND_ORDER.indexOf(kind);
    return i === -1 ? KIND_ORDER.length : i;
  };
  return {
    valueOf: (c) => valueFor(c, "kind"),
    compare: (a, b) => rank(a) - rank(b) || a.localeCompare(b),
  };
}

export interface ClusterGroup {
  label: string;
  startIndex: number;
  clusters: Cluster[];
}

// The group whose range contains the given flat index into `ordered`.
export function groupContaining(
  groups: ClusterGroup[],
  index: number,
): ClusterGroup | undefined {
  return groups.find(
    (g) => index >= g.startIndex && index < g.startIndex + g.clusters.length,
  );
}

// Reorder into contiguous groups, returning the flat array plus each group's
// range so flat-index selection / keyboard nav is unaffected. Untagged last.
// A dimension can override how values are derived and ordered (e.g. rollout's
// implicit catch-all group and rung comparator); plain tag value and
// alphabetical order otherwise.
export function groupClusters(
  clusters: Cluster[],
  dim: string,
  grouping?: {
    valueOf: (c: Cluster) => string | null;
    compare: (a: string, b: string) => number;
  },
): { ordered: Cluster[]; groups: ClusterGroup[] } {
  const valueOf = grouping?.valueOf ?? ((c: Cluster) => valueFor(c, dim));
  const compare =
    grouping?.compare ?? ((a: string, b: string) => a.localeCompare(b));
  const buckets = new Map<string | null, Cluster[]>();
  for (const c of clusters) {
    const v = valueOf(c);
    if (!buckets.has(v)) buckets.set(v, []);
    buckets.get(v)!.push(c);
  }
  const keys = [...buckets.keys()].sort((a, b) => {
    if (a === null) return 1;
    if (b === null) return -1;
    return compare(a, b);
  });

  const ordered: Cluster[] = [];
  const groups: ClusterGroup[] = [];
  for (const k of keys) {
    const members = buckets.get(k)!;
    groups.push({
      label: k ?? "(untagged)",
      startIndex: ordered.length,
      clusters: members,
    });
    ordered.push(...members);
  }
  return { ordered, groups };
}

// Approval methods sort in the order the operations palette lists them.
const APPROVAL_ORDER = ["auto", "manual", "external_execution"];

// Grouping by an operation's approval method, so it's obvious at a glance
// which clusters run the op without an operator approving it first.
export function approvalGrouping(op: string): {
  valueOf: (c: Cluster) => string | null;
  compare: (a: string, b: string) => number;
} {
  const rank = (behavior: string) => {
    const i = APPROVAL_ORDER.indexOf(behavior);
    return i === -1 ? APPROVAL_ORDER.length : i;
  };
  return {
    valueOf: (c) => c.possible_operations?.[op]?.approval_behavior ?? null,
    compare: (a, b) => rank(a) - rank(b) || a.localeCompare(b),
  };
}
